/**
 * sectionLabel — pure helper for formatting writer-facing section
 * labels at the EventLog write boundary.
 *
 * Single source of truth for "what does the writer see when an event
 * is scoped to a section?". Producers (sseAdapter, shell dispatchers)
 * call this when they have a sectionId and a section list; consumers
 * (messageProjection, formatEventTitle) read the resulting
 * ``EventLogEntry.sectionLabel`` field with a fallback chain.
 *
 * SRP: pure data transform. No React, no I/O, no logging. Tested
 * directly so the format is pinned without driving the producers.
 *
 * # DECISION: ``§${number}`` is the canonical writer-facing form.
 * # WHY: matches the rest of the addin's vocabulary — filters
 * #      (``/filter §9.2``), inspector titles (``§9.2 Discussion of
 * #      Study Design``), spine number gutter ("9.1", "9.2"). The
 * #      backend's ``module_id`` (e.g. ``"FRM-607"``) is a stable
 * #      identifier writers don't recognise. Every producer has
 * #      access to the section list at write time, so we capture
 * #      the writer-facing form once and persist it with the entry.
 * # DATE: 2026-04-27
 *
 * # DECISION: returns ``null`` when the lookup fails, NOT a fallback
 * #   like ``"section"`` or the raw sectionId.
 * # WHY: the consumer (projection / formatTitle) owns the fallback
 * #      chain. A null here is honest — "no label resolved" — and
 * #      lets the consumer decide whether to surface the raw id
 * #      (timeline detail, debug) or a generic word (chat: "section").
 * #      Returning a string here would conflate "label" with
 * #      "fallback display".
 * # DATE: 2026-04-27
 */

import { displaySectionNumber } from "@shared/client/sectionLabel";

/** Minimum shape the lookup needs. Defined here (not imported from
 *  useVariantCRun) so this module has zero React dependencies. The
 *  caller passes whatever section list it has; structural typing
 *  matches as long as ``id`` and ``number`` are present. */
export interface SectionLabelSource {
  id: string;
  number: string;
}

/**
 * Core formatter — turns a section's display number into the
 * canonical writer-facing label. Single source of truth for the
 * ``§${number}`` shape; both call paths
 * (``formatSectionLabel`` and the adapter's meta-driven path) share it.
 *
 * Returns null for null/empty input so callers can compose without
 * extra guards.
 */
export function formatSectionLabelFromNumber(sectionNumber: string | null | undefined): string | null {
  // ``"TITLE"`` is the front-matter sentinel (module has no real section
  // number), not a labelable number — treat it like empty input so callers
  // fall back to the section title instead of rendering ``§TITLE``.
  if (!sectionNumber || sectionNumber === "TITLE") return null;
  return `§${sectionNumber}`;
}

/**
 * The spine gutter shows a module's section number ("2", "5.1"). Front-matter
 * modules with no number carry the "TITLE" sentinel instead — render nothing
 * for those rather than the raw sentinel. Kept keyed on the exact sentinel
 * (matching the Word-insertion guards) so doc types with a genuinely numbered
 * title page (IB ``A.2.1``, ICF ``0``) still show their number.
 *
 * A module-id slug (an id carrying a ``/``, e.g. the ANDA-leaf sub-sections
 * ``3.2.P.1/description``) is an internal key, never a reader-facing number —
 * ``displaySectionNumber`` drops it, matching the Word renderer and guru-web's
 * ``SpineRail``. Without this the gutter would print the raw slug (or, before
 * the server sends the module's own id, the shared leaf root repeated on every
 * row — the "doc number prepended to every title" bug).
 */
export function formatGutterNumber(number: string | null | undefined): string {
  // ``TITLE`` is not a slug, so guard it here (the shared helper keeps it).
  if (!number || number === "TITLE") return "";
  // A private workspace template's unnumbered heading: its number is an
  // internal id the document never shows, so neither does the spine.
  if (unnumberedSections.has(number)) return "";
  return displaySectionNumber(number);
}

/** Section numbers of the open doctype whose heading renders WITHOUT a number
 *  — a private workspace template's unnumbered headings (``heading_numbering:
 *  template``). Set by the shell whenever the doctype config changes; empty
 *  for every public doctype. Read by the spine gutter and by the live
 *  re-projection (which resolves these headings by title as well). */
let unnumberedSections: ReadonlySet<string> = new Set();

/** Heading titles of those sections, for surfaces that name one without the
 *  spine's title column beside it (the chat scope chip). */
let unnumberedTitles: ReadonlyMap<string, string> = new Map();

export function setUnnumberedSections(
  numbers: Iterable<string> | null | undefined,
  titles?: Iterable<readonly [string, string]> | null,
): void {
  unnumberedSections = new Set(numbers ?? []);
  unnumberedTitles = new Map(
    [...(titles ?? [])].filter(([n]) => unnumberedSections.has(n)).map(([n, t]) => [n, t] as [string, string]),
  );
}

export function isUnnumberedSection(number: string | null | undefined): boolean {
  return !!number && unnumberedSections.has(number);
}

/** The heading title of an unnumbered section, or null (numbered, unknown). */
export function unnumberedSectionTitle(number: string | null | undefined): string | null {
  return number ? (unnumberedTitles.get(number) ?? null) : null;
}

/**
 * Resolve a writer-facing label for a section id.
 *
 *   * ``sectionId === null`` → ``null`` (run-wide event, no scope).
 *   * ``sectionId`` not found in ``sections`` → ``null`` (caller
 *     decides the fallback display).
 *   * Match found → ``§${section.number}``.
 *
 * Pure. Linear scan over ``sections``; fine for the dozens-to-low-
 * hundreds size of a doctype's section list.
 */
export function formatSectionLabel(
  sectionId: string | null,
  sections: ReadonlyArray<SectionLabelSource>,
): string | null {
  if (sectionId === null) return null;
  const match = sections.find((s) => s.id === sectionId);
  if (!match) return null;
  return formatSectionLabelFromNumber(match.number);
}
